import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SectionWrapper, SectionHeader } from '@/components/common';

interface Problem {
  title: string;
  description: string;
}

interface ProblemsSectionProps {
  title?: string;
  subtitle?: string;
  problems?: Problem[];
  serviceType?: string;
  color?: string;
  ctaLink?: string;
  ctaText?: string;
  className?: string;
}

export function ProblemsSection({
  title,
  subtitle,
  problems,
  serviceType,
  color = 'text-blue-600',
  ctaLink,
  ctaText,
  className,
}: ProblemsSectionProps) {
  const { t } = useTranslation('services');

  // If problems not provided, build from translations
  const displayProblems = problems || (serviceType ? [1, 2, 3, 4].map((num) => ({
    title: t(`${serviceType}.problems.problem${num}.title`),
    description: t(`${serviceType}.problems.problem${num}.description`),
  })) : []);

  const displayTitle = title || (serviceType ? t(`${serviceType}.problems.title`) : '');
  const displaySubtitle = subtitle || (serviceType ? t(`${serviceType}.problems.subtitle`) : '');
  const displayCtaLink = ctaLink || (serviceType ? `/search?type=${serviceType}` : '/search');
  const displayCtaText = ctaText || t('findPro');

  return (
    <SectionWrapper bgColor="bg-gray-50" className={className}>
      <SectionHeader
        title={displayTitle}
        subtitle={displaySubtitle}
        subtitleClassName="text-lg text-gray-600 max-w-2xl mx-auto"
        className="mb-12"
      />

      <div className="grid md:grid-cols-2 gap-6">
        {displayProblems.map((problem, index) => (
          <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {problem.title}
            </h3>
            <p className="text-gray-600 mb-4">
              {problem.description}
            </p>
            <Link
              to={displayCtaLink}
              className={`inline-flex items-center gap-1 text-sm ${color} font-medium hover:underline`}
            >
              {displayCtaText}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}
